import { useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { SignIn } from './SignIn'
import { SignUp } from './SignUp'
import type { InvitationInfo, Session } from '@shared/types'

/**
 * /join?invitation=…  The link from the partner's email.
 *
 * Three states of the person holding it: no account yet (create one, with
 * the invited address fixed), an account but signed out (sign in), or signed
 * in -- in which case a single button accepts. The email confirmation for a
 * new account comes back here, so the flow finishes where it started.
 */
export function JoinPage({ session, onJoined }: { session: Session | null; onJoined: (s: Session) => void }) {
  const id = new URLSearchParams(window.location.search).get('invitation') ?? ''
  const [info, setInfo] = useState<InvitationInfo | null | 'missing'>(null)
  const [mode, setMode] = useState<'choose' | 'signin' | 'signup'>('choose')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!id) {
      setInfo('missing')
      return
    }
    api
      .invitation(id)
      .then(setInfo)
      .catch(() => setInfo('missing'))
  }, [id])

  async function accept() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const next = await api.acceptInvitation(id)
      window.history.replaceState(null, '', '/')
      onJoined(next)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not accept the invitation.')
    } finally {
      setBusy(false)
    }
  }

  if (info === null) {
    return (
      <div className="lock">
        <p className="muted">Loading…</p>
      </div>
    )
  }

  if (info === 'missing' || info.status !== 'pending') {
    const why =
      info === 'missing'
        ? 'This invitation link is not one we recognise.'
        : info.status === 'accepted'
          ? 'This invitation has already been accepted.'
          : 'This invitation has expired.'
    return (
      <div className="lock">
        <div className="lock-card">
          <div className="lock-logo" aria-hidden="true">
            🥪
          </div>
          <h1>Five Lunches</h1>
          <p className="sub">{why}</p>
          <p className="muted" style={{ fontSize: 14 }}>
            Ask whoever invited you to send a new one from Settings, or{' '}
            <a href="/">sign in</a> if you are already in.
          </p>
        </div>
      </div>
    )
  }

  if (mode === 'signin') {
    return <SignIn onSignedIn={onJoined} intro={`Sign in to join ${info.familyName}.`} />
  }
  if (mode === 'signup') {
    return <SignUp lockedEmail={info.email} callbackURL={`/join?invitation=${encodeURIComponent(id)}`} />
  }

  const wrongAccount = session && session.email.toLowerCase() !== info.email.toLowerCase()
  // Already in this family: the invitation has nothing left to do.
  const alreadyHere = session?.familyId === info.familyId
  // In a different one: joining moves the app, and there is no switcher yet.
  const elsewhere = session?.familyId && !alreadyHere ? session.familyName : null

  return (
    <div className="lock">
      <div className="lock-card">
        <div className="lock-logo" aria-hidden="true">
          🥪
        </div>
        <h1>Join {info.familyName}</h1>
        <p className="sub">{info.inviterName} invited you to plan lunches together.</p>

        <div className="stack">
          {error && <div className="banner error">{error}</div>}

          {session ? (
            <>
              {wrongAccount && (
                <div className="banner info">
                  This invitation went to <b>{info.email}</b>, but you are signed in as {session.email}.
                  Sign out and back in with the invited address.
                </div>
              )}
              {alreadyHere ? (
                <>
                  <p className="muted" style={{ fontSize: 14, margin: 0 }}>
                    You are already in {info.familyName}. Nothing to do here.
                  </p>
                  <a className="btn primary" href="/">
                    Open the app
                  </a>
                </>
              ) : (
                <>
                  {elsewhere && !wrongAccount && (
                    <div className="banner error">
                      You already plan with <b>{elsewhere}</b>. Joining {info.familyName} moves your app to
                      it, and you will not see {elsewhere} again until there is a family switcher. Email
                      hello@fivelunches.com to undo it.
                    </div>
                  )}
                  <button className="btn primary" onClick={accept} disabled={busy || Boolean(wrongAccount)}>
                    {busy ? 'Joining…' : elsewhere ? `Leave ${elsewhere} and join` : `Join as ${session.name}`}
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 14, margin: 0 }}>
                Sent to <b>{info.email}</b>. New here? Create an account for that address. Already
                have one? Sign in.
              </p>
              <button className="btn primary" onClick={() => setMode('signup')}>
                Create my account
              </button>
              <button className="btn" onClick={() => setMode('signin')}>
                I have an account
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
